import { useState, type FormEvent } from 'react';
import type { SessionUserDto } from '@hesabat/contracts';
import { api, InvalidCodeError, ProblemError, RateLimitedError, UnauthorizedError } from './api';
import Hint from './Hint';

interface Props {
  user: SessionUserDto;
  /** بعد تغيير البريد أو إيقاف الخطوتين: الواجهة تعيد قراءة الجلسة. */
  onChanged: () => void;
  /** تفعيل الدخول بخطوتين يفتح شاشة التسجيل نفسها اللى بتظهر عند أول دخول. */
  onEnableMfa: () => void;
  onSessionLost: () => void;
}

const ISSUE_LABELS: Record<string, string> = {
  PASSWORD_INCORRECT: 'كلمة السر الحالية غير صحيحة',
  EMAIL_UNCHANGED: 'ده نفس البريد الحالى',
};

/** نص الخطأ للمستخدم — والجلسة المنتهية ترجّعه لشاشة الدخول. */
function failureOf(caught: unknown, onSessionLost: () => void): string | null {
  if (caught instanceof UnauthorizedError) {
    onSessionLost();
    return null;
  }
  if (caught instanceof InvalidCodeError) return 'الرمز غير صحيح. اكتب الرمز الحالى من التطبيق.';
  if (caught instanceof RateLimitedError) return 'محاولات كتير. استنى دقيقة وجرّب تانى.';
  if (caught instanceof ProblemError) {
    const issues = caught.problem.issues ?? [];
    return issues.length > 0
      ? issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message).join('، ')
      : (caught.problem.detail ?? caught.problem.title);
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/** الاسم الظاهر فى الشريط العلوى وسجل العمليات — بلا كلمة سر لأنه لا يمس الدخول. */
export function ChangeName({ user, onChanged, onSessionLost }: Props) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const trimmed = displayName.trim();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!trimmed || trimmed === user.displayName) return;
    setBusy(true);
    setFailure(null);
    setDone(false);
    try {
      await api.changeName({ displayName: trimmed });
      setDone(true);
      onChanged();
    } catch (caught) {
      setFailure(failureOf(caught, onSessionLost));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
      <h2>الاسم</h2>
      <div className="form-row">
        <label className="grow">
          اسمك فى البرنامج
          <input
            autoComplete="name"
            maxLength={200}
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <Hint>بيظهر فوق فى الشريط، وجنب كل عملية فى سجل العمليات.</Hint>
        </label>
      </div>
      {failure && <p className="error">{failure}</p>}
      {done && <p className="sub">اتغيّر الاسم.</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="primary"
          disabled={busy || !trimmed || trimmed === user.displayName}
        >
          {busy ? 'جارى الحفظ…' : 'حفظ الاسم'}
        </button>
      </div>
    </form>
  );
}

/** بريد الدخول — كلمة السر الحالية شرط، والدخول التالى بالبريد الجديد. */
export function ChangeEmail({ user, onChanged, onSessionLost }: Props) {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const invalid = newEmail !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim());

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (invalid || !newEmail || !currentPassword) return;
    setBusy(true);
    setFailure(null);
    setDone(false);
    try {
      await api.changeEmail({ newEmail: newEmail.trim(), currentPassword });
      setNewEmail('');
      setCurrentPassword('');
      setDone(true);
      onChanged();
    } catch (caught) {
      setFailure(failureOf(caught, onSessionLost));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
      <h2>بريد الدخول</h2>
      <p className="sub">
        البريد الحالى: <strong dir="ltr">{user.email}</strong>
      </p>
      <div className="form-row">
        <label className="grow">
          البريد الجديد
          <input
            type="email"
            dir="ltr"
            autoComplete="email"
            className={invalid ? 'invalid' : ''}
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
          />
          <Hint>هتدخل بيه من المرة الجاية، ورابط «نسيت كلمة السر» هيوصل عليه.</Hint>
        </label>
        <label className="grow">
          كلمة السر الحالية
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </label>
      </div>
      {invalid && <p className="error">البريد مش مكتوب صح</p>}
      {failure && <p className="error">{failure}</p>}
      {done && <p className="sub">اتغيّر البريد. ادخل بيه من المرة الجاية.</p>}
      <div className="form-actions">
        <button
          type="submit"
          className="primary"
          disabled={busy || invalid || !newEmail || !currentPassword}
        >
          {busy ? 'جارى الحفظ…' : 'تغيير البريد'}
        </button>
      </div>
    </form>
  );
}

/**
 * الدخول بخطوتين: إيقافه بكلمة السر ورمز التطبيق معًا، وتفعيله بشاشة التسجيل.
 * مشغّل المنصة مفروض عليه، فلا زر إيقاف له.
 */
export function TwoStepLogin({ user, onChanged, onEnableMfa, onSessionLost }: Props) {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!currentPassword || code.trim().length < 6) return;
    setBusy(true);
    setFailure(null);
    try {
      await api.disableMfa({ currentPassword, code: code.trim() });
      setCurrentPassword('');
      setCode('');
      setOpen(false);
      onChanged();
    } catch (caught) {
      setFailure(failureOf(caught, onSessionLost));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (!user.mfa.enabled) {
    return (
      <div className="panel">
        <h2>الدخول بخطوتين</h2>
        <p className="sub">
          <span className="pill muted">متوقف</span> الدخول بكلمة السر بس.
        </p>
        <Hint>
          لما تفعّله، البرنامج هيطلب منك عند الدخول رمز من تطبيق على الموبايل (Google Authenticator
          مثلاً)، فلو حد عرف كلمة السر مش هيقدر يدخل.
        </Hint>
        <div className="form-actions">
          <button type="button" className="primary" onClick={onEnableMfa}>
            تفعيل الدخول بخطوتين
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
      <h2>الدخول بخطوتين</h2>
      <p className="sub">
        <span className="pill">مفعّل</span> البرنامج بيطلب رمز التطبيق عند كل دخول.
      </p>
      {!user.mfa.canDisable && <Hint>مفروض على حساب مشغّل المنصة، ومايتوقفش.</Hint>}
      {user.mfa.canDisable && !open && (
        <div className="form-actions">
          <button type="button" className="ghost" onClick={() => setOpen(true)}>
            إيقاف الدخول بخطوتين
          </button>
        </div>
      )}
      {user.mfa.canDisable && open && (
        <>
          <Hint>
            بعد الإيقاف هتدخل بكلمة السر بس، وأى حد يعرفها يقدر يدخل. أكواد الاسترداد القديمة هتبطل.
          </Hint>
          <div className="form-row">
            <label className="grow">
              كلمة السر الحالية
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </label>
            <label className="grow">
              الرمز من التطبيق (أو كود استرداد)
              <input
                inputMode="numeric"
                dir="ltr"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </label>
          </div>
          {failure && <p className="error">{failure}</p>}
          <div className="form-actions">
            <button
              type="submit"
              className="danger"
              disabled={busy || !currentPassword || code.trim().length < 6}
            >
              {busy ? 'جارى الإيقاف…' : 'إيقاف'}
            </button>
            <button type="button" className="ghost" onClick={() => setOpen(false)} disabled={busy}>
              رجوع
            </button>
          </div>
        </>
      )}
    </form>
  );
}
